import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useState } from "react";
import Spinner from "../spinner/spinner";

// Enable / disable / delete-user confirmations were four near-identical files
// across System Management and Training. Only the verb and the thunk differed,
// so the action stays at the call site — the payload shape differs too — and
// everything else lives here once.

export interface ConfirmUserActionDialogProps {
  open: boolean;
  /** Dialog title, e.g. "Disable User". */
  title: string;
  /** Verb completing "Are you sure you want to …?" — lower case, e.g. "disable". */
  verb: string;
  /** The row being acted on: user_name, first_name, last_name. */
  user: any;
  loading?: boolean;
  /** Dispatch and resolve to the thunk result. */
  onConfirm: () => Promise<any>;
  /** Message on success; empty string when simply dismissed. */
  onClose: (message: string) => void;
}

const ConfirmUserActionDialog = ({
  open,
  title,
  verb,
  user,
  loading,
  onConfirm,
  onClose,
}: ConfirmUserActionDialogProps) => {
  const [error, setError] = useState("");

  const close = (message: string, reason?: string) => {
    if (reason === "backdropClick") return;
    setError("");
    onClose(message);
  };

  const submit = () => {
    onConfirm().then((result: any) => {
      if (result.payload?.result === true) close(result.payload.message);
      else setError(result.payload?.message || `Could not ${verb} this user.`);
    });
  };

  return (
    <Dialog
      fullWidth={true}
      maxWidth="md"
      onClose={(_, reason) => close("", reason)}
      open={open}
    >
      <DialogTitle
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(59, 147, 74, 1)",
        }}
      >
        <Typography
          variant="h5"
          component="span"
          sx={{ fontWeight: "bold", color: "white" }}
        >
          {title}
        </Typography>
        <IconButton aria-label="close" onClick={() => close("")} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Spinner loading={Boolean(loading)} />
      <DialogContent>
        <DialogContentText>
          <Grid container item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
            {/* The verb finishes the question, so it never has to read around
                a mid-sentence "email:" — "…want to remove from LWS and disable
                email: x" was the version that prompted this. */}
            Are you sure you want to {verb}?
          </Grid>
          <Grid container item xs={12} sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            <span style={{ fontWeight: "bold" }}>
              {[user?.first_name, user?.last_name].filter(Boolean).join(" ")}
            </span>
            &ensp;&mdash;&ensp;
            <span style={{ fontWeight: "bold" }}>{user?.user_name}</span>
          </Grid>
          {error && (
            <Grid container item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
              <Typography sx={{ color: "red" }}>{error}</Typography>
            </Grid>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ borderTop: "1px solid" }}>
        <Button
          variant="outlined"
          onClick={() => close("")}
          sx={{ padding: "5px 10px 5px 10px", backgroundColor: "red", color: "black" }}
        >
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={submit}
          sx={{
            padding: "5px 10px 5px 10px",
            backgroundColor: "rgba(59, 147, 74, 1)",
            color: "white",
          }}
        >
          submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmUserActionDialog;
